import { createResourceOwnership } from "@afar-research/shared/resourceOwnership";
import type { ResourceLifetime, ResourceReleaseReason } from "@afar-research/shared/resourceLedger";
import type { OwnedResourceLease } from "@afar-research/shared/resourceOwnership";

import { clientResourceLedger } from "./connection/resources";

export const workSurfaceResources = createResourceOwnership({
  ledger: clientResourceLedger,
});

const objectUrlResources = new Map<string, OwnedResourceLease>();
const releasedObjectUrls = new Set<string>();
const releasedObjectUrlOrder: string[] = [];
const RELEASED_OBJECT_URL_TOMBSTONE_LIMIT = 256;

function rememberReleasedObjectUrl(url: string): void {
  if (releasedObjectUrls.has(url)) return;
  releasedObjectUrls.add(url);
  releasedObjectUrlOrder.push(url);
  if (releasedObjectUrlOrder.length <= RELEASED_OBJECT_URL_TOMBSTONE_LIMIT) return;
  const evicted = releasedObjectUrlOrder.shift();
  if (evicted !== undefined) releasedObjectUrls.delete(evicted);
}

export function createOwnedObjectUrl(
  blob: Blob,
  input: {
    readonly ownerIdentity: string;
    readonly resourceIdentity: string;
    readonly lifetime: Extract<ResourceLifetime, "open-owner" | "retained-owner">;
  },
): string {
  const url = URL.createObjectURL(blob);
  releasedObjectUrls.delete(url);
  objectUrlResources.get(url)?.release("replacement");
  const resource = workSurfaceResources.acquire(
    {
      owner: { kind: "workspace-surface", identity: input.ownerIdentity },
      resource: { kind: "object-url", identity: input.resourceIdentity },
      lifetime: input.lifetime,
    },
    () => URL.revokeObjectURL(url),
  );
  objectUrlResources.set(url, resource);
  return url;
}

export function releaseOwnedObjectUrl(
  url: string,
  reason: ResourceReleaseReason = "owner-disposal",
): void {
  const resource = objectUrlResources.get(url);
  if (!resource) {
    if (releasedObjectUrls.has(url)) return;
    URL.revokeObjectURL(url);
    return;
  }
  objectUrlResources.delete(url);
  resource.release(reason);
  rememberReleasedObjectUrl(url);
}
